// ============================================================
// LinuxBackupRestore.jsx
//
// Branded, responsive course content page for the
// "Linux Backup & Restore" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import BackupIcon              from "@mui/icons-material/Backup";
import RestoreIcon             from "@mui/icons-material/Restore";
import StorageIcon             from "@mui/icons-material/Storage";
import FolderOpenIcon          from "@mui/icons-material/FolderOpen";
import ArchiveIcon             from "@mui/icons-material/Archive";
import UnarchiveIcon           from "@mui/icons-material/Unarchive";
import SyncIcon                from "@mui/icons-material/Sync";
import CameraAltIcon           from "@mui/icons-material/CameraAlt";
import ScheduleIcon            from "@mui/icons-material/Schedule";
import CloudUploadIcon         from "@mui/icons-material/CloudUpload";
import SecurityIcon            from "@mui/icons-material/Security";
import LayersIcon              from "@mui/icons-material/Layers";
import BuildIcon               from "@mui/icons-material/Build";
import TerminalIcon            from "@mui/icons-material/Terminal";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import DnsIcon                 from "@mui/icons-material/Dns";
import ArticleIcon             from "@mui/icons-material/Article";
import FilterListIcon          from "@mui/icons-material/FilterList";
import SpeedIcon               from "@mui/icons-material/Speed";
import LockIcon                from "@mui/icons-material/Lock";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours — change here to retheme everything.
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Tool accent colours — each backup tool gets its own colour
    toolRsync:   "#60a5fa",   // blue
    toolTar:     "#fb923c",   // orange
    toolTimeshift:"#a78bfa",  // violet
    toolDd:      "#f472b6",   // pink
    toolDump:    "#4ade80",   // green
    toolBorg:    "#facc15",   // yellow
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── MUI TableCell style helper ────────────────────────────
const tcStyle = (extra = {}) => ({
    borderColor: BRAND.border,
    color: BRAND.muted,
    fontSize: 13,
    py: 1.4,
    ...extra,
});

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold icon circle + heading — reused by every section.
//  Props:
//    icon  – MUI icon element
//    label – heading text string
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card for one backup concept.
//  Props:
//    icon        – MUI icon element
//    title       – concept name
//    description – plain-English explanation
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2, py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* Decorative macOS dots */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button */}
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && (
                            <Typography
                                sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}
                            >
                                # {comment}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 12, md: 13 },
                                color: BRAND.codeText,
                                wordBreak: "break-all",
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox
//  Red-tinted panel for important warnings.
// ============================================================
function WarningBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                p: 2, mb: 2,
                background: BRAND.danger,
                border: `1px solid ${BRAND.dangerBorder}`,
                borderRadius: 2,
            }}
        >
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: SuccessBox
//  Green-tinted best-practice tip box.
// ============================================================
function SuccessBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                p: 2, mb: 2,
                background: BRAND.success,
                border: `1px solid ${BRAND.successBorder}`,
                borderRadius: 2,
            }}
        >
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for optional deeper content.
//  Props:
//    title    – header label
//    icon     – MUI icon element
//    children – body JSX
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5, py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open
                    ? <ExpandLessIcon sx={{ color: BRAND.gold }} />
                    : <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                }
            </Box>

            {/* MUI Collapse gives a smooth open/close animation */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5, py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ToolHeader
//  Coloured header banner used at the top of each tool section.
//  Props:
//    icon     – MUI icon element
//    name     – tool name (e.g. "rsync")
//    tagline  – one-line description
//    color    – accent colour from BRAND
// ============================================================
function ToolHeader({ icon, name, tagline, color }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 3,
                background: `${color}0d`,
                border: `1px solid ${color}33`,
                borderRadius: 2,
            }}
        >
            {/* Large tool icon */}
            <Box
                sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    background: `${color}18`,
                    border: `1px solid ${color}45`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
            </Box>
            <Box>
                {/* Tool name in accent colour */}
                <Typography
                    sx={{
                        fontFamily: "'Georgia', serif",
                        fontWeight: 800,
                        fontSize: { xs: 16, md: 18 },
                        color,
                        lineHeight: 1.2,
                    }}
                >
                    {name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: BRAND.muted, mt: 0.3 }}>
                    {tagline}
                </Typography>
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row.
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2, mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxBackupRestore
//  Full "Linux Backup & Restore" lesson page.
// ============================================================
export default function LinuxBackupRestore() {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture — consistent across all lesson pages
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Systems · Module 07"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>Backup &amp; Restore</span>
                    </Typography>

                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 680,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        Data loss is not a matter of <em>if</em> — it is a matter of{" "}
                        <em>when</em>. Disks fail, configs break, and ransomware exists.
                        A solid backup strategy is the difference between a 10-minute
                        recovery and a career-ending incident. This lesson covers Linux's
                        most powerful backup tools — from fast incremental syncs to
                        full disk images — and teaches you how to test your restores.
                    </Typography>

                    {/* Quick stats */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[
                            { label: "~3 hours",     sub: "read time"   },
                            { label: "Intermediate", sub: "level"        },
                            { label: "5 tools",      sub: "covered"      },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5, py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <BackupIcon />,
                                title: "Full vs Incremental vs Differential",
                                description:
                                    "A full backup copies everything. An incremental backup copies only what changed since the last backup. A differential copies everything changed since the last full. rsync does incremental; tar does full; Borg does incremental with deduplication.",
                            },
                            {
                                icon: <SyncIcon />,
                                title: "3-2-1 Backup Rule",
                                description:
                                    "Keep 3 copies of your data, on 2 different media types, with 1 copy stored off-site (cloud or remote server). This protects against hardware failure, theft, fire, and ransomware simultaneously.",
                            },
                            {
                                icon: <CameraAltIcon />,
                                title: "Snapshots",
                                description:
                                    "A snapshot captures the exact state of a filesystem or disk at a point in time. Snapshots are fast and space-efficient because they record only changes (copy-on-write). Timeshift, LVM, and Btrfs all support snapshots.",
                            },
                            {
                                icon: <RestoreIcon />,
                                title: "Testing Restores",
                                description:
                                    "A backup you haven't tested is not a backup — it is hope. Regularly restore files to a test location to verify your backups are valid, complete, and recoverable within your target time window.",
                            },
                            {
                                icon: <LockIcon />,
                                title: "Encryption",
                                description:
                                    "Backup data should be encrypted, especially off-site or cloud backups. Tools like Borg and Restic encrypt by default. For tar archives, pipe through gpg or use openssl to add encryption.",
                            },
                            {
                                icon: <StorageIcon />,
                                title: "Backup Destinations",
                                description:
                                    "Local disk (fast, convenient), external USB (portable), NAS/network share (always-on), remote SSH server (off-site), and cloud storage (S3, Backblaze B2) each have different cost/speed/reliability trade-offs.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – RSYNC
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SyncIcon />} label="rsync — Incremental File Sync" />

                    <ToolHeader
                        icon={<SyncIcon />}
                        name="rsync"
                        tagline="Fast, incremental, bandwidth-efficient file synchronisation over local or SSH"
                        color={BRAND.toolRsync}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>rsync</code> is the Swiss Army knife of
                        Linux backups. It compares source and destination and transfers only the differences
                        — making repeat backups fast. It preserves permissions, timestamps, symlinks, and
                        ownership. It also works over SSH, making remote backups effortless.
                    </Typography>

                    {/* rsync flag reference table */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Key rsync Flags
                    </Typography>
                    <TableContainer
                        component={Box}
                        sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 3 }}
                    >
                        <Table size="small" sx={{ minWidth: 380 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Flag", "Long form", "Meaning"].map((h) => (
                                        <TableCell key={h} sx={tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border })}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { flag: "-a", long: "--archive",          meaning: "Archive mode: preserves permissions, timestamps, symlinks, owner, group" },
                                    { flag: "-v", long: "--verbose",          meaning: "Print each file being transferred" },
                                    { flag: "-h", long: "--human-readable",   meaning: "Show sizes in KB/MB/GB instead of bytes" },
                                    { flag: "-z", long: "--compress",         meaning: "Compress data during transfer (useful over slow networks)" },
                                    { flag: "-P", long: "--progress",         meaning: "Show per-file progress bar and allow resuming interrupted transfers" },
                                    { flag: "-n", long: "--dry-run",          meaning: "Simulate the transfer — show what would happen without changing anything" },
                                    { flag: "--delete", long: "",             meaning: "Delete files in destination that no longer exist in source (mirror mode)" },
                                    { flag: "--exclude", long: "",            meaning: "Exclude files or patterns: --exclude='*.tmp' --exclude='.cache'" },
                                    { flag: "--backup", long: "",             meaning: "Keep old versions of replaced files (pair with --backup-dir)" },
                                    { flag: "-e ssh", long: "",               meaning: "Use SSH as transport for remote transfers" },
                                    { flag: "--bwlimit=1000", long: "",       meaning: "Throttle bandwidth to 1000 KB/s (protect production networks)" },
                                ].map(({ flag, long, meaning }) => (
                                    <TableRow key={flag} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                        <TableCell sx={tcStyle({ color: BRAND.goldBright, fontFamily: "monospace", fontWeight: 700, borderColor: BRAND.border })}>{flag}</TableCell>
                                        <TableCell sx={tcStyle({ fontFamily: "monospace", color: BRAND.muted, fontSize: 12, borderColor: BRAND.border })}>{long}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{meaning}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Core rsync commands */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Local Backups
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ rsync -avh /home/user/ /mnt/backup/home/",            comment: "Sync home directory to a local backup drive" },
                            { cmd: "$ rsync -avh --delete /home/user/ /mnt/backup/home/",   comment: "Mirror mode: destination becomes identical to source" },
                            { cmd: "$ rsync -avhn /home/user/ /mnt/backup/home/",           comment: "Dry run first — always test before a real sync" },
                            { cmd: "$ rsync -avh --exclude='*.log' /var/ /mnt/backup/var/", comment: "Backup /var but exclude all .log files" },
                            { cmd: "$ rsync -avh --exclude={'.cache','.tmp'} /home/user/ /mnt/backup/home/", comment: "Exclude multiple patterns with brace expansion" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Remote Backups over SSH
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ rsync -avhz -e ssh /home/user/ deploy@192.168.1.10:/backups/home/",      comment: "Push local files to a remote server over SSH" },
                            { cmd: "$ rsync -avhz -e ssh deploy@192.168.1.10:/var/www/ /mnt/backup/www/",      comment: "Pull remote files to local machine" },
                            { cmd: "$ rsync -avhz -e 'ssh -p 2222' /data/ user@remote.host:/backups/data/",   comment: "Use a non-standard SSH port with -p" },
                            { cmd: "$ rsync -avhz --bwlimit=2000 /data/ user@remote:/backups/",               comment: "Throttle to 2 MB/s to avoid saturating the network" },
                        ]}
                    />

                    <ExpandableExample title="Versioned backups with rsync + --backup-dir" icon={<SyncIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            The <code style={{ color: BRAND.goldBright }}>--backup</code> and{" "}
                            <code style={{ color: BRAND.goldBright }}>--backup-dir</code> flags let rsync keep
                            previous versions of overwritten files in a separate dated folder. Combined with a
                            daily cron job, this gives you rolling versioned backups without a dedicated
                            backup application.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: `$ DATE=$(date +%Y-%m-%d)`,                                              comment: "Store today's date in a variable" },
                                { cmd: `$ rsync -avh --delete \\`,                                              comment: "Start the rsync command (backslash continues on next line)" },
                                { cmd: `    --backup --backup-dir=/mnt/backup/versions/$DATE \\`,               comment: "Put overwritten/deleted files in a dated folder" },
                                { cmd: `    /home/user/ /mnt/backup/current/`,                                  comment: "Main backup destination" },
                            ]}
                        />
                        <SuccessBox>
                            The result: <code>/mnt/backup/current/</code> always mirrors today's source.
                            Deleted or changed files are moved to{" "}
                            <code>/mnt/backup/versions/2025-01-15/</code> — giving you point-in-time recovery
                            without duplicating unchanged files.
                        </SuccessBox>
                    </ExpandableExample>

                    <ExpandableExample title="Automated rsync backup with cron" icon={<ScheduleIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Add this to your crontab with <code style={{ color: BRAND.goldBright }}>crontab -e</code>{" "}
                            to run an rsync backup automatically every night at 2 AM.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "# In crontab -e:",                                                       },
                                { cmd: "0 2 * * * rsync -avh --delete /home/user/ /mnt/backup/home/ >> /var/log/backup-home.log 2>&1", comment: "Run at 2 AM, log output" },
                                { cmd: "0 3 * * * rsync -avhz -e ssh /var/www/ deploy@192.168.1.10:/backups/www/ >> /var/log/backup-www.log 2>&1", comment: "Remote backup at 3 AM" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – TAR
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ArchiveIcon />} label="tar — Compressed Archives" />

                    <ToolHeader
                        icon={<ArchiveIcon />}
                        name="tar"
                        tagline="Create, compress, inspect, and extract archive files — the universal Linux archive format"
                        color={BRAND.toolTar}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>tar</code> (Tape ARchive) bundles many
                        files into one and optionally compresses them. It is the standard format for
                        distributing source code, backing up directories, and moving large file trees.
                        The flags look cryptic at first — the mnemonic is{" "}
                        <strong style={{ color: BRAND.white }}>"Create/eXtract Zee (gzip) Very Frankly"</strong>.
                    </Typography>

                    {/* tar flag reference */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Key tar Flags
                    </Typography>
                    <TableContainer
                        component={Box}
                        sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflowX: "auto", mb: 3 }}
                    >
                        <Table size="small" sx={{ minWidth: 360 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Flag", "Meaning"].map((h) => (
                                        <TableCell key={h} sx={tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border })}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { flag: "-c", meaning: "Create a new archive" },
                                    { flag: "-x", meaning: "Extract files from an archive" },
                                    { flag: "-t", meaning: "List the contents of an archive without extracting" },
                                    { flag: "-z", meaning: "Compress/decompress with gzip (.tar.gz / .tgz)" },
                                    { flag: "-j", meaning: "Compress/decompress with bzip2 (.tar.bz2) — smaller but slower than gzip" },
                                    { flag: "-J", meaning: "Compress/decompress with xz (.tar.xz) — best compression, slowest" },
                                    { flag: "-v", meaning: "Verbose — print each file name as it is processed" },
                                    { flag: "-f", meaning: "Specify the archive filename — must be immediately followed by the filename" },
                                    { flag: "-C", meaning: "Change to this directory before extracting (sets the extract destination)" },
                                    { flag: "-p", meaning: "Preserve permissions when extracting (needed for system restores)" },
                                    { flag: "--exclude", meaning: "Exclude files matching a pattern: --exclude='*.log'" },
                                ].map(({ flag, meaning }) => (
                                    <TableRow key={flag} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                        <TableCell sx={tcStyle({ color: BRAND.goldBright, fontFamily: "monospace", fontWeight: 700, borderColor: BRAND.border })}>{flag}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{meaning}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Creating archives */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Creating Archives
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ tar -czvf backup.tar.gz /home/user/",                  comment: "Create gzip-compressed archive of /home/user/" },
                            { cmd: "$ tar -cjvf backup.tar.bz2 /home/user/",                 comment: "Create bzip2-compressed archive (smaller, slower)" },
                            { cmd: "$ tar -cJvf backup.tar.xz /home/user/",                  comment: "Create xz-compressed archive (best compression)" },
                            { cmd: "$ tar -czvf backup.tar.gz --exclude='*.log' /var/www/",  comment: "Archive /var/www/ but skip .log files" },
                            { cmd: "$ tar -czvf backup-$(date +%Y%m%d).tar.gz /home/user/",  comment: "Date-stamp the filename: backup-20250115.tar.gz" },
                        ]}
                    />

                    {/* Extracting archives */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Inspecting &amp; Extracting Archives
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ tar -tzvf backup.tar.gz",                              comment: "List archive contents without extracting" },
                            { cmd: "$ tar -xzvf backup.tar.gz",                              comment: "Extract to current directory" },
                            { cmd: "$ tar -xzvf backup.tar.gz -C /restore/",                 comment: "Extract to a specific directory" },
                            { cmd: "$ tar -xzvf backup.tar.gz home/user/config.txt",         comment: "Extract only one specific file from the archive" },
                            { cmd: "$ tar -xzvf backup.tar.gz -C /restore/ --strip-components=1", comment: "Strip the first path component when extracting" },
                        ]}
                    />

                    <ExpandableExample title="Encrypted tar archives with gpg" icon={<LockIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            For sensitive data, pipe tar output through{" "}
                            <code style={{ color: BRAND.goldBright }}>gpg</code> to encrypt the archive
                            symmetrically with a passphrase. The result is a single encrypted file that
                            cannot be read without the passphrase.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ tar -czf - /home/user/ | gpg -c -o backup-encrypted.tar.gz.gpg", comment: "Create encrypted archive (will prompt for passphrase)" },
                                { cmd: "$ gpg -d backup-encrypted.tar.gz.gpg | tar -xzf - -C /restore/",   comment: "Decrypt and extract in one pipeline" },
                                { cmd: "$ gpg --list-keys",                                                  comment: "List GPG keys if using public-key encryption" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Splitting large archives across multiple files" icon={<StorageIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            When backing up to FAT32 media (4 GiB file limit) or splitting for cloud upload,
                            pipe tar through <code style={{ color: BRAND.goldBright }}>split</code>.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ tar -czf - /home/user/ | split -b 2G - backup-part-",             comment: "Split into 2 GiB chunks: backup-part-aa, backup-part-ab..." },
                                { cmd: "$ cat backup-part-* | tar -xzf - -C /restore/",                     comment: "Reassemble and extract" },
                                { cmd: "$ ls -lh backup-part-*",                                             comment: "Check sizes of all parts" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – TIMESHIFT
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CameraAltIcon />} label="Timeshift — System Snapshots" />

                    <ToolHeader
                        icon={<CameraAltIcon />}
                        name="Timeshift"
                        tagline="System restore point tool — like Windows System Restore but for Linux"
                        color={BRAND.toolTimeshift}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Timeshift takes point-in-time snapshots of your{" "}
                        <strong style={{ color: BRAND.white }}>system files</strong> — not user data.
                        It is designed to protect against broken updates, misconfigured packages, or
                        accidental system file deletion. It uses either{" "}
                        <code style={{ color: BRAND.goldBright }}>rsync</code> (on ext4) or{" "}
                        <code style={{ color: BRAND.goldBright }}>Btrfs</code> subvolume snapshots.
                        Install with <code style={{ color: BRAND.goldBright }}>sudo apt install timeshift</code>.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Timeshift CLI Commands
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo apt install timeshift",                                     comment: "Install Timeshift (Ubuntu/Debian)" },
                            { cmd: `$ sudo timeshift --create --comments "Before system upgrade"`,     comment: "Create a snapshot with a descriptive comment" },
                            { cmd: "$ sudo timeshift --list",                                           comment: "List all available snapshots with dates and sizes" },
                            { cmd: "$ sudo timeshift --restore",                                        comment: "Interactive restore — choose snapshot from a menu" },
                            { cmd: "$ sudo timeshift --restore --snapshot '2025-01-15_02-00-01'",      comment: "Restore a specific snapshot by name" },
                            { cmd: "$ sudo timeshift --delete --snapshot '2025-01-01_02-00-01'",       comment: "Delete an old snapshot to free disk space" },
                            { cmd: "$ sudo timeshift --check",                                          comment: "Check scheduled snapshots and run any that are due" },
                        ]}
                    />

                    <SuccessBox>
                        Always create a Timeshift snapshot <strong>before</strong> a major system upgrade
                        (<code>sudo apt dist-upgrade</code>), installing new drivers, or editing critical
                        config files like <code>/etc/fstab</code> or <code>/etc/grub</code>. It takes under
                        a minute and could save hours of recovery work.
                    </SuccessBox>

                    <ExpandableExample title="Automating Timeshift with scheduled snapshots" icon={<ScheduleIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Timeshift has built-in scheduling via its GUI or config file. You can also trigger
                            snapshots from cron for more control. The config file is at{" "}
                            <code style={{ color: BRAND.goldBright }}>/etc/timeshift/timeshift.json</code>.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo timeshift --snapshot-device /dev/sdb1",        comment: "Store snapshots on a specific device" },
                                { cmd: "$ cat /etc/timeshift/timeshift.json",                  comment: "View current Timeshift configuration" },
                                { cmd: "# In crontab -e — daily snapshot at 1 AM:",                                                            },
                                { cmd: `0 1 * * * sudo timeshift --create --comments "auto-daily-$(date +%Y%m%d)" >> /var/log/timeshift.log 2>&1` },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – DD (DISK IMAGING)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="dd — Full Disk Imaging" />

                    <ToolHeader
                        icon={<StorageIcon />}
                        name="dd"
                        tagline="Bit-for-bit disk cloning — creates complete disk images including partition tables and boot sectors"
                        color={BRAND.toolDd}
                    />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 2 }}>
                        <code style={{ color: BRAND.goldBright }}>dd</code> reads and writes raw data
                        block-by-block. It produces a perfect clone of an entire disk or partition —
                        including the partition table, bootloader, and every byte of data. The resulting
                        image can be compressed and stored, then written back to restore the exact original state.
                    </Typography>

                    <WarningBox>
                        <strong>dd is nicknamed "disk destroyer"</strong> because swapping{" "}
                        <code>if=</code> (input) and <code>of=</code> (output) wipes your source.
                        Always double-check: <code>if</code> = source, <code>of</code> = destination.
                        Use <code>lsblk</code> to confirm device names before running.
                    </WarningBox>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Disk Cloning & Imaging
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo dd if=/dev/sda of=/mnt/backup/sda.img bs=4M status=progress",   comment: "Clone entire disk to a file (bs=4M = 4 MiB block size for speed)" },
                            { cmd: "$ sudo dd if=/dev/sda of=/dev/sdb bs=4M status=progress",               comment: "Clone disk directly to another disk (sda → sdb)" },
                            { cmd: "$ sudo dd if=/dev/sda1 of=/mnt/backup/sda1.img bs=4M status=progress",  comment: "Image a single partition only" },
                            { cmd: "$ sudo dd if=/dev/sda bs=512 count=1 of=/mnt/backup/mbr.img",           comment: "Backup the MBR (first 512 bytes) — includes partition table" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Compressed Imaging
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo dd if=/dev/sda bs=4M | gzip -c > /mnt/backup/sda.img.gz",       comment: "Pipe through gzip to compress on the fly (saves space)" },
                            { cmd: "$ gunzip -c /mnt/backup/sda.img.gz | sudo dd of=/dev/sda bs=4M",        comment: "Decompress and restore to disk" },
                            { cmd: "$ sudo dd if=/dev/sda bs=4M | gzip -c | split -b 4G - /mnt/backup/sda-", comment: "Compress and split into 4 GiB chunks" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Restoring from Images
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo dd if=/mnt/backup/sda.img of=/dev/sda bs=4M status=progress",   comment: "Restore full disk from image file" },
                            { cmd: "$ sudo dd if=/mnt/backup/mbr.img of=/dev/sda bs=512 count=1",           comment: "Restore only the MBR (partition table + bootloader)" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – BORG & RESTIC (MODERN TOOLS)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="Borg &amp; Restic — Modern Backup Tools" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        For production servers and serious personal backups, dedicated tools like{" "}
                        <strong style={{ color: BRAND.white }}>BorgBackup</strong> and{" "}
                        <strong style={{ color: BRAND.white }}>Restic</strong> go far beyond rsync and tar.
                        They offer <strong style={{ color: BRAND.white }}>deduplication</strong> (identical
                        blocks stored once across all backups), <strong style={{ color: BRAND.white }}>
                        encryption by default</strong>, and efficient incremental backups to any destination
                        including cloud storage.
                    </Typography>

                    <ExpandableExample title="BorgBackup — deduplicated, encrypted backups" icon={<SecurityIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Borg stores backups as encrypted, deduplicated archives in a{" "}
                            <em>repository</em>. Each backup run creates a new named archive but only stores
                            new or changed blocks — dramatically reducing storage use over time.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo apt install borgbackup",                                      comment: "Install BorgBackup" },
                                { cmd: "$ borg init --encryption=repokey /mnt/backup/borg-repo",             comment: "Initialise a new encrypted Borg repository" },
                                { cmd: `$ borg create /mnt/backup/borg-repo::backup-$(date +%Y%m%d) /home/user/`, comment: "Create a named backup archive" },
                                { cmd: "$ borg list /mnt/backup/borg-repo",                                  comment: "List all archives in the repository" },
                                { cmd: "$ borg extract /mnt/backup/borg-repo::backup-20250115 --strip-components=2", comment: "Extract a specific archive" },
                                { cmd: "$ borg info /mnt/backup/borg-repo",                                  comment: "Show repository stats including total savings from dedup" },
                                { cmd: "$ borg prune --keep-daily=7 --keep-weekly=4 /mnt/backup/borg-repo",  comment: "Prune old archives: keep 7 daily, 4 weekly" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Restic — encrypted backups to any cloud" icon={<CloudUploadIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Restic encrypts and deduplicates backups, and supports many backends: local disk,
                            SSH, S3, Google Cloud Storage, Azure Blob, and Backblaze B2. It is written in Go
                            and ships as a single binary with no dependencies.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo apt install restic",                                          comment: "Install Restic" },
                                { cmd: "$ restic init --repo /mnt/backup/restic-repo",                      comment: "Initialise a new encrypted repository" },
                                { cmd: "$ restic -r /mnt/backup/restic-repo backup /home/user/",             comment: "Run a backup" },
                                { cmd: "$ restic -r /mnt/backup/restic-repo snapshots",                     comment: "List all snapshots" },
                                { cmd: "$ restic -r /mnt/backup/restic-repo restore latest --target /restore/", comment: "Restore the latest snapshot" },
                                { cmd: "$ restic -r s3:s3.amazonaws.com/my-bucket backup /home/user/",      comment: "Backup directly to AWS S3" },
                                { cmd: "$ restic -r /mnt/backup/restic-repo forget --keep-last 10 --prune", comment: "Keep only the 10 most recent snapshots" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – BACKUP STRATEGY
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="Backup Strategy Best Practices" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Having a tool is not a strategy. Here is a checklist of professional backup
                        practices that separate reliable systems from ones that fail at the worst moment.
                    </Typography>

                    {/* Strategy cards grid */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            {
                                number: "01",
                                title: "Follow the 3-2-1 Rule",
                                body: "3 copies, 2 media types, 1 off-site. This single rule protects against every common failure scenario simultaneously.",
                                color: BRAND.toolRsync,
                            },
                            {
                                number: "02",
                                title: "Automate Everything",
                                body: "Manual backups are not backups — they are good intentions. Use cron or systemd timers. If you have to remember to do it, eventually you'll forget.",
                                color: BRAND.toolTar,
                            },
                            {
                                number: "03",
                                title: "Test Your Restores",
                                body: "Monthly, restore a random file to /tmp and verify its contents. Quarterly, practise a full system restore in a VM. A backup you haven't tested is worthless.",
                                color: BRAND.toolTimeshift,
                            },
                            {
                                number: "04",
                                title: "Encrypt Off-Site Backups",
                                body: "Any backup that leaves your physical control must be encrypted. Use Borg, Restic, or pipe tar through gpg. Never store plaintext backups in the cloud.",
                                color: BRAND.toolDd,
                            },
                            {
                                number: "05",
                                title: "Keep Multiple Versions",
                                body: "A single copy that mirrors today is not a backup — it just mirrors your mistakes too. Retain 7 daily, 4 weekly, and 12 monthly snapshots as a minimum.",
                                color: BRAND.toolBorg,
                            },
                            {
                                number: "06",
                                title: "Monitor and Alert",
                                body: "Your backup script should log success or failure. Set up alerts for failures — silent failure is worse than no backup because you don't know you have a problem.",
                                color: BRAND.goldBright,
                            },
                        ].map(({ number, title, body, color }) => (
                            <Grid item xs={12} sm={6} key={number}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        height: "100%",
                                        background: BRAND.surface,
                                        border: `1px solid ${BRAND.border}`,
                                        borderRadius: 3,
                                        transition: "border-color 0.25s, box-shadow 0.25s",
                                        "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                        {/* Large number badge */}
                                        <Typography
                                            sx={{
                                                fontFamily: "'Georgia', serif",
                                                fontWeight: 800,
                                                fontSize: 28,
                                                color: `${color}55`,
                                                lineHeight: 1,
                                                flexShrink: 0,
                                                mt: 0.2,
                                            }}
                                        >
                                            {number}
                                        </Typography>
                                        <Box>
                                            <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14, mb: 0.6 }}>
                                                {title}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                                                {body}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Quick production backup script */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Production Backup Script Template
                    </Typography>
                    <Box
                        sx={{
                            background: BRAND.codeBg,
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            p: 2.5,
                            overflowX: "auto",
                        }}
                    >
                        {[
                            { line: "#!/bin/bash",                                                             },
                            { line: "# backup.sh — daily incremental backup with logging and error handling",   isComment: true },
                            { line: "",                                                                          },
                            { line: "DATE=$(date +%Y-%m-%d_%H%M)",                                             },
                            { line: 'LOG="/var/log/backup-$DATE.log"',                                          },
                            { line: 'SOURCE="/home/user/"',                                                     },
                            { line: 'DEST="/mnt/backup/current/"',                                              },
                            { line: 'VERSIONS="/mnt/backup/versions/$DATE"',                                    },
                            { line: "",                                                                          },
                            { line: "echo \"[$DATE] Starting backup...\" | tee -a $LOG",                        },
                            { line: "",                                                                          },
                            { line: "rsync -avh --delete \\",                                                   },
                            { line: "  --backup --backup-dir=$VERSIONS \\",                                     },
                            { line: "  $SOURCE $DEST >> $LOG 2>&1",                                             },
                            { line: "",                                                                          },
                            { line: "if [ $? -eq 0 ]; then",                                                    },
                            { line: '  echo "[SUCCESS] Backup completed." | tee -a $LOG',                       },
                            { line: "else",                                                                      },
                            { line: '  echo "[FAILURE] Backup failed — check $LOG" | tee -a $LOG',              },
                            { line: '  # Optionally send alert: mail -s "Backup Failed" admin@example.com < $LOG', isComment: true },
                            { line: "fi",                                                                        },
                        ].map((entry, i) => (
                            <Typography
                                key={i}
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: { xs: 11, md: 12 },
                                    color: entry.isComment ? "rgba(255,255,255,0.3)" : BRAND.codeText,
                                    lineHeight: 1.9,
                                    whiteSpace: "pre",
                                }}
                            >
                                {entry.line || "\u00A0"}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 8 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "The 3-2-1 backup rule and why it protects against every failure scenario",
                            "How rsync works incrementally and the meaning of its key flags",
                            "How to run local and remote rsync backups, including over SSH",
                            "How to create versioned backups with rsync --backup-dir",
                            "How to create, list, and extract tar archives with all compression formats",
                            "How to encrypt tar archives by piping through gpg",
                            "How to take and restore Timeshift system snapshots from the CLI",
                            "How dd creates bit-perfect disk images and how to restore them safely",
                            "How BorgBackup and Restic provide deduplication and encryption",
                            "How to automate backups with cron and log success or failure",
                            "How to write a production backup script with error handling",
                            "Why testing restores regularly is as important as taking backups",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 9 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://linuxize.com/post/how-to-use-rsync-for-local-and-remote-data-transfer-and-synchronization/"
                        label="Using rsync — Linuxize"
                        sub="Thorough guide covering all rsync use cases including remote backups and scheduling."
                    />
                    <ResourceLink
                        href="https://www.geeksforgeeks.org/tar-command-in-linux-with-examples/"
                        label="tar Command — GeeksforGeeks"
                        sub="Reference covering all tar flags with practical create and extract examples."
                    />
                    <ResourceLink
                        href="https://teejeetech.com/timeshift/"
                        label="Timeshift — Official Site"
                        sub="Official documentation for Timeshift including rsync and Btrfs snapshot modes."
                    />
                    <ResourceLink
                        href="https://borgbackup.readthedocs.io/en/stable/"
                        label="BorgBackup — Official Documentation"
                        sub="Full Borg reference: deduplication internals, encryption options, and remote repos."
                    />
                    <ResourceLink
                        href="https://restic.readthedocs.io/en/stable/"
                        label="Restic — Official Documentation"
                        sub="Covers all backends (S3, B2, Azure, GCS, SSH) and the snapshot lifecycle."
                    />
                    <ResourceLink
                        href="https://man7.org/linux/man-pages/man1/rsync.1.html"
                        label="rsync(1) — Linux Man Page"
                        sub="The complete rsync man page — every flag and option documented exhaustively."
                    />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box
                    sx={{
                        mt: 2, p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Start simple —
                        a daily rsync cron job to an external drive is infinitely better than no backup.
                        Then layer in off-site with Restic to Backblaze B2 ($0.006/GB/month). Once that's
                        running, add Timeshift for system snapshots. Build your backup strategy incrementally,
                        and always{" "}
                        <strong style={{ color: BRAND.white }}>test a restore</strong> before you need it.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
